import { Grid } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import * as React from 'react';
import { useObservable } from 'react-use-observable';
import { Space } from 'v2/components/Shared/Space';
import financialService from 'v2/services/financial';

import { useStyles } from './styles';
import { CAIXA_ECONOMICA_ID } from '../../form';

import type { NewBankAccount } from '../../form';

type DataDisplayProps = {
  values: NewBankAccount;
  onPreviewClick: (fieldName: string) => void;
};

function DataDisplay({ values, onPreviewClick }: DataDisplayProps) {
  const classes = useStyles();
  const [banks] = useObservable(() => financialService.getBanks(), []);

  const isDocumentCpf = cpf.isValid(values.document_number);
  const hasSelectedRG = values.personal_document === 'RG';

  if (!banks) return null;

  const selectedBank = banks?.find((bank) => bank.id === values.bank_id);
  const bankName = selectedBank?.value.replace(/[0-9]/g, '').trim();

  return (
    <Grid className={classes.content} container spacing={2}>
      <Grid item xs={12}>
        <h4 className={classes.sectionTitle}>Dados pessoais</h4>
        <div className={classes.card}>
          <p className={classes.fieldTitle} data-testid='name-label'>
            Nome
          </p>
          <Space axis='vertical' size={5} />
          <p className={classes.fieldValue} data-testid='name-value'>
            {values.name}
          </p>
          <Space axis='vertical' size={16} />
          <p className={classes.fieldTitle} data-testid='document-label'>
            {isDocumentCpf ? 'CPF' : 'CNPJ'}
          </p>
          <Space axis='vertical' size={5} />
          <p className={classes.fieldValue} data-testid='document-value'>
            {isDocumentCpf ? cpf.format(values.document_number) : cnpj.format(values.document_number)}
          </p>
        </div>
      </Grid>
      {(values.rg_front_image || values.rg_back_image || values.cnh_image) && (
        <Grid item xs={12}>
          <h4 className={classes.sectionTitle}>Documentos</h4>
          <div className={classes.card}>
            <p className={classes.fieldTitle} data-testid='personal-document-label'>
              {hasSelectedRG ? 'RG' : 'CNH'}
            </p>
            <Space axis='vertical' size={16} />
            <div className={classes.btnContainer}>
              {hasSelectedRG ? (
                <>
                  <Button id='rg-front-preview-btn' variant='outlined' onClick={() => onPreviewClick('rg_front_image')}>
                    Visualizar frente RG
                  </Button>
                  <Button id='rg-back-preview-btn' variant='outlined' onClick={() => onPreviewClick('rg_back_image')}>
                    Visualizar verso RG
                  </Button>
                </>
              ) : (
                <Button id='cnh-preview-btn' variant='outlined' onClick={() => onPreviewClick('cnh_image')}>
                  Visualizar CNH
                </Button>
              )}
            </div>
          </div>
        </Grid>
      )}
      {values.selfie_video && (
        <Grid item xs={12}>
          <h4 className={classes.sectionTitle}>Vídeo</h4>
          <div className={classes.card}>
            <p className={classes.fieldTitle}>Vídeo selfie</p>
            <Space axis='vertical' size={16} />
            <Button id='video-preview-btn' variant='outlined' onClick={() => onPreviewClick('selfie_video')}>
              Visualizar vídeo
            </Button>
          </div>
        </Grid>
      )}
      <Grid item xs={12}>
        <h4 className={classes.sectionTitle}>Conta bancária</h4>
        <div className={classes.card}>
          <p className={classes.fieldTitle} data-testid='account-type-label'>
            <strong>{values.type === 'CC' ? 'Conta corrente' : 'Conta poupança'}</strong>
          </p>
          <Space axis='vertical' size={16} />
          <p className={classes.fieldTitle} data-testid='bank-name-value'>
            {bankName}
          </p>
          <Space axis='vertical' size={8} />
          <div className={classes.bankData} data-testid='bank-data-value'>
            <p>Agência: {values.agency}</p>
            {values.agency_digit && <p>Dígito da agência: {values.agency_digit}</p>}
            <p>Conta: {values.account}</p>
            <p>Dígito da conta: {values.account_digit}</p>
            {values.bank_id === CAIXA_ECONOMICA_ID && <p>Operação: {values.operation_id}</p>}
          </div>
          {values.bank_receipt_image && (
            <>
              <Space axis='vertical' size={16} />
              <Button id='bank-receipt-preview-btn' variant='outlined' onClick={() => onPreviewClick('bank_receipt_image')}>
                Visualizar comprovante
              </Button>
            </>
          )}
        </div>
      </Grid>
    </Grid>
  );
}

export { DataDisplay };
